import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRoles } from '@/contexts/RolesContext';
import type { EntitlementDetail } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Info } from 'lucide-react';
import { SubscribeButton } from '@/components/billing/SubscribeButton';
import { RedeemTrialCodeButton } from '@/components/billing/RedeemTrialCodeButton';
import { PastDueAlertContent } from '@/components/billing/PastDueAlertContent';
import { ManageAddOnsDialog } from '@/components/billing/ManageAddOnsDialog';

interface EntitlementsTableProps {
  entitlements: EntitlementDetail[];
  hasStripeCustomer: boolean;
}

/**
 * Displays a table of all active entitlements with their type, profile allocation, and status.
 */
export function EntitlementsTable({ entitlements, hasStripeCustomer }: EntitlementsTableProps) {
  const { t, language } = useLanguage();
  const { isAdmin, isBillingManager } = useRoles();
  const hasActiveSubscription = entitlements.some((e) => e.isSubscription);

  const locale = useMemo(() => {
    switch (language) {
      case 'fr':
        return 'fr-FR';
      case 'de':
        return 'de-DE';
      default:
        return 'en-US';
    }
  }, [language]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t.billing.entitlementsTable.never;
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEntitlementType = (entitlement: EntitlementDetail) => {
    if (entitlement.isTrial) return t.billing.entitlementsTable.trial;
    if (entitlement.isSubscription) return t.billing.entitlementsTable.paid;
    if (entitlement.isDefault) return t.billing.entitlementsTable.trial;
    return t.billing.entitlementsTable.paid;
  };

  const getTypeBadgeVariant = (entitlement: EntitlementDetail) => {
    if (entitlement.isTrial) return 'secondary' as const;
    if (entitlement.isSubscription) return 'default' as const;
    if (entitlement.isDefault) return 'outline' as const;
    return 'default' as const;
  };

  const getProfilesDisplay = (entitlement: EntitlementDetail) => {
    const value = entitlement.value;
    if (entitlement.ruleType === 'minimum') return `≥ ${String(value)}`;
    return String(value);
  };

  const getProfilesTooltip = (entitlement: EntitlementDetail) => {
    const value = entitlement.value;
    if (entitlement.ruleType === 'minimum') {
      return t.billing.entitlementsTable.ruleTypes.minimumTooltip.replace('{value}', String(value));
    }
    if (entitlement.ruleType === 'cumulative') {
      return t.billing.entitlementsTable.ruleTypes.cumulativeTooltip.replace('{value}', String(value));
    }
    return null;
  };

  const isExpired = (entitlement: EntitlementDetail) => {
    if (!entitlement.expirationDate) return false;
    return new Date(entitlement.expirationDate) < new Date();
  };

  const isPastDue = (entitlement: EntitlementDetail) => {
    return entitlement.isSubscription && entitlement.subscriptionStatus === 'past_due';
  };

  const getStatusBadgeVariant = (entitlement: EntitlementDetail) => {
    if (isPastDue(entitlement)) return 'destructive' as const;
    if (isExpired(entitlement)) return 'destructive' as const;
    return 'default' as const;
  };

  const getStatus = (entitlement: EntitlementDetail) => {
    if (isPastDue(entitlement)) return t.billing.entitlementsTable.pastDue;
    if (isExpired(entitlement)) return t.billing.entitlementsTable.expired;
    return t.billing.entitlementsTable.active;
  };

  const getRenewalText = (entitlement: EntitlementDetail) => {
    const expired = isExpired(entitlement);
    const date = entitlement.expirationDate ? formatDate(entitlement.expirationDate) : null;

    if (expired && date) {
      return date;
    }

    if (entitlement.isSubscription && entitlement.subscriptionAutoRenews && date) {
      return t.billing.entitlementsTable.renewsOn.replace('{date}', date);
    }

    if (date) {
      return t.billing.entitlementsTable.expiresOn.replace('{date}', date);
    }

    return t.billing.entitlementsTable.neverExpires;
  };

  if (entitlements.length === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t.billing.entitlementsTable.title}</CardTitle>
          <div className="flex gap-2">
            <RedeemTrialCodeButton />
            {(isAdmin || isBillingManager) && <SubscribeButton variant={'outline'} size={'sm'} />}
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-8 text-center text-muted-foreground">
            {t.billing.entitlementsTable.noEntitlements}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t.billing.entitlementsTable.title}</CardTitle>
        <div className="flex gap-2">
          <RedeemTrialCodeButton />
          {(isAdmin || isBillingManager) && hasActiveSubscription && (
            <ManageAddOnsDialog hasStripeCustomer={hasStripeCustomer} />
          )}
          {(isAdmin || isBillingManager) && !hasActiveSubscription && <SubscribeButton variant={'outline'} size={'sm'} />}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.billing.entitlementsTable.type}</TableHead>
              <TableHead>{t.billing.entitlementsTable.profiles}</TableHead>
              <TableHead>{t.billing.entitlementsTable.status}</TableHead>
              <TableHead>{t.billing.entitlementsTable.renewal}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entitlements.map((entitlement) => {
              const tooltip = getProfilesTooltip(entitlement);
              return (
                <TableRow key={entitlement.id}>
                  <TableCell>
                    <Badge variant={getTypeBadgeVariant(entitlement)}>
                      {getEntitlementType(entitlement)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    {tooltip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 cursor-help">
                            {getProfilesDisplay(entitlement)}
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {tooltip}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      getProfilesDisplay(entitlement)
                    )}
                  </TableCell>
                  <TableCell>
                    {isPastDue(entitlement) ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Badge variant="destructive" className="cursor-pointer">
                            {getStatus(entitlement)}
                          </Badge>
                        </PopoverTrigger>
                        <PopoverContent className="w-96 p-0">
                          <PastDueAlertContent />
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Badge variant={getStatusBadgeVariant(entitlement)}>
                        {getStatus(entitlement)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {getRenewalText(entitlement)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
